import { Controller } from 'react-hook-form';
import {
  InputText,
  DateInput,
  Checkbox,
  Select,
  InputSize,
  DateInputSize,
  PickerPosition,
  SelectSize,
  SelectListPosition,
  Autocomplete,
} from '@ui/src';
import { AccessRight, SignatureType } from '../../../hoc/common/types';
import { NatureOfControlEnum } from '../../../hoc/new-application/types';
import CreateFormRow from '../../common/create-form-row/create-form-row';
import { CreateCompanyOfficialFormTypes } from '../types';

import cn from './overview.module.css';

const CreateCompanyOfficialForm = ({
  control,
  havingControl,
  cofError,
  errors,
  sumSubError,
  applicationSchemes,
  countries,
}: CreateCompanyOfficialFormTypes) => (
  <div className={cn.createCompanyOfficialForm}>
    <div className={cn.createCompanyOfficialFormTitle}>Contacts</div>
    <CreateFormRow title="First name">
      <Controller
        control={control}
        name="firstName"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="firstName"
            className={errors?.firstName?.message ? '' : 'borderless'}
            placeholder="Enter First Name"
            onChange={onChange}
            value={value}
            error={errors?.firstName?.message}
            size={InputSize.SMALL}
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="Last name">
      <Controller
        control={control}
        name="lastName"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="lastName"
            className={errors?.lastName?.message ? '' : 'borderless'}
            placeholder="Enter Last Name"
            onChange={onChange}
            value={value}
            error={errors?.lastName?.message}
            size={InputSize.SMALL}
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="Phone number">
      <Controller
        control={control}
        name="phone"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="phone"
            className={errors?.phone?.message ? '' : 'borderless'}
            placeholder="Enter Phone"
            onChange={onChange}
            value={value}
            size={InputSize.SMALL}
            error={errors?.phone?.message}
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="E-mail">
      <Controller
        control={control}
        name="email"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="email"
            className={errors?.email?.message ? '' : 'borderless'}
            placeholder="Enter Email"
            onChange={onChange}
            value={value}
            size={InputSize.SMALL}
            error={errors?.email?.message}
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="Date of Birth">
      <Controller
        control={control}
        name="dob"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <DateInput
            value={value}
            onChange={(date) => onChange(date.toISOString())}
            placeholder="Select Date Of Birth"
            size={DateInputSize.SMALL}
            pickerPisition={PickerPosition.RIGHT}
            pickerClassName={cn.dateOfBirthPicker}
            className={errors?.dob?.message ? cn.dobError : ''}
            error={errors?.dob?.message}
            borderless={!errors?.dob?.message}
            birthdayPicker
            withoutCustomSelector
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="Nationality">
      <Controller
        control={control}
        name="nationalityId"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            list={countries}
            name="nationalityId"
            selectWrapperClassName={cn.createCompanyOfficialSelect}
            value={value}
            error={errors?.nationalityId?.message}
            placeholder="Select Country"
            onChange={onChange}
            size={SelectSize.SMALL}
            withClearButton
            borderless
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="Position">
      <Controller
        control={control}
        name="position"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="position"
            placeholder="Enter position"
            onChange={onChange}
            value={value}
            size={InputSize.SMALL}
            className={errors?.position?.message ? '' : 'borderless'}
            error={errors?.position?.message}
          />
        )}
      />
    </CreateFormRow>
    <div className={cn.checkboxWrapper}>
      <Controller
        control={control}
        name="havingControl"
        render={({ field: { onChange, value } }) => (
          <Checkbox
            type="checkbox"
            name="havingControl"
            checked={value}
            title="Having a control in company"
            onChange={(e) => onChange(e.target.checked)}
          />
        )}
      />
    </div>
    {havingControl && (
      <>
        <CreateFormRow title="Percentage of control">
          <Controller
            control={control}
            name="percentage"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <InputText
                name={name}
                value={value}
                placeholder="Enter Percentage of control"
                onChange={onChange}
                size={InputSize.SMALL}
                className="borderless"
                error={errors?.percentage?.message}
              />
            )}
          />
        </CreateFormRow>
        <CreateFormRow title="Nature of control">
          <Controller
            control={control}
            name="natureOfControl"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                list={[
                  {
                    name: 'Direct Control',
                    value: NatureOfControlEnum.DIRECT_CONTROL,
                  },
                  {
                    name: 'Indirect Control',
                    value: NatureOfControlEnum.INDIRECT_CONTROL,
                  },
                ]}
                name="natureOfControl"
                selectWrapperClassName={cn.createCompanyOfficialSelect}
                value={value}
                error={errors?.natureOfControl?.message}
                placeholder="Select Nature of Control"
                onChange={onChange}
                size={SelectSize.SMALL}
                borderless
              />
            )}
          />
        </CreateFormRow>
      </>
    )}
    <CreateFormRow title="Signature type">
      <Controller
        control={control}
        name="signatureType"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <Select
            list={[
              {
                name: 'None',
                value: SignatureType.NONE,
              },
              {
                name: 'Single',
                value: SignatureType.SINGLE,
              },
            ]}
            name="signatureType"
            value={value}
            placeholder="Select Signature Type"
            selectWrapperClassName={cn.createCompanyOfficialSelect}
            onChange={onChange}
            size={SelectSize.SMALL}
            error={errors?.signatureType?.message}
            borderless
            fullWidth
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="Company official type">
      <Controller
        control={control}
        name="companyOfficialType"
        defaultValue="COMPANY_OFFICIAL"
        render={({ field: { onChange, value } }) => (
          <Select
            list={[
              {
                name: 'Company Official',
                value: 'COMPANY_OFFICIAL',
              },
              {
                name: 'Main representative',
                value: 'MAIN_REPRESENTATIVE',
              },
            ]}
            name="companyOfficialType"
            value={value}
            placeholder="Select company official type"
            selectWrapperClassName={cn.createCompanyOfficialSelect}
            onChange={onChange}
            size={SelectSize.SMALL}
            error={errors?.companyOfficialType?.message}
            borderless
            fullWidth
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="Access Rights">
      <Controller
        control={control}
        name="accessRight"
        defaultValue={AccessRight.VIEWER}
        render={({ field: { onChange, value } }) => (
          <Select
            list={[
              {
                name: 'None',
                value: AccessRight.NONE,
              },
              {
                name: 'Viewer',
                value: AccessRight.VIEWER,
              },
            ]}
            name="accessRight"
            value={value}
            placeholder="Select access right"
            selectWrapperClassName={cn.createCompanyOfficialSelect}
            onChange={onChange}
            size={SelectSize.SMALL}
            error={errors?.accessRight?.message}
            borderless
            fullWidth
          />
        )}
      />
    </CreateFormRow>
    <CreateFormRow title="SumSub application ID">
      <Controller
        control={control}
        name="applicantId"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="applicantId"
            className={
              sumSubError || errors?.applicantId?.message ? '' : 'borderless'
            }
            placeholder="Enter SumSub application ID"
            onChange={onChange}
            value={value}
            size={InputSize.SMALL}
            error={sumSubError || errors?.applicantId?.message}
          />
        )}
      />
    </CreateFormRow>
    {applicationSchemes && applicationSchemes?.length > 0 && (
      <>
        <div className={cn.createCompanyOfficialFormTitle}>
          Application Scheme
        </div>
        <CreateFormRow title="COF scheme">
          <Controller
            control={control}
            name="applicationSchemeId"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                name="applicationSchemeId"
                placeholder="Select an option"
                list={applicationSchemes}
                value={value}
                size={SelectSize.SMALL}
                onChange={onChange}
                tabIndex={0}
                error={errors?.applicationSchemeId?.message}
                selectListPosition={SelectListPosition.TOP}
                withClearButton
                borderless
              />
            )}
          />
        </CreateFormRow>
      </>
    )}

    {cofError && <div className={cn.cofError}>{cofError}</div>}
  </div>
);

export default CreateCompanyOfficialForm;
